import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit, Eye, EyeOff, Save, X, Image, Bold, Italic, Type } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editLang, setEditLang] = useState('de');
  const editorRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    title_en: '',
    content: '',
    content_en: '',
    featured_image: '',
    is_published: true
  });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/blog/posts?published_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      if (editingPost) {
        await axios.put(`${API_URL}/api/blog/posts/${editingPost.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Beitrag aktualisiert');
      } else {
        await axios.post(`${API_URL}/api/blog/posts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Beitrag erstellt');
      }
      loadPosts();
      resetForm();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Beitrag wirklich löschen?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/blog/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Beitrag gelöscht');
      loadPosts();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      title_en: post.title_en || '',
      content: post.content || '',
      content_en: post.content_en || '',
      featured_image: post.featured_image || '',
      is_published: post.is_published
    });
    setShowEditor(true);
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      title_en: '',
      content: '',
      content_en: '',
      featured_image: '',
      is_published: true
    });
    setShowEditor(false);
  };

  // Rich text editor functions
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      const field = editLang === 'en' ? 'content_en' : 'content';
      setFormData(prev => ({ ...prev, [field]: editorRef.current.innerHTML }));
    }
  };

  // Update editor content when language changes or when editing a post
  useEffect(() => {
    if (editorRef.current && showEditor) {
      const content = editLang === 'en' ? formData.content_en : formData.content;
      if (editorRef.current.innerHTML !== content) {
        editorRef.current.innerHTML = content || '';
      }
    }
  }, [editLang, showEditor, editingPost]);

  const insertImage = () => {
    const url = prompt('Bild-URL eingeben:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="admin-blog">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl">Blog & News</h1>
          <p className="text-[#666666]">{posts.length} Beiträge</p>
        </div>
        <Button
          onClick={() => setShowEditor(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="new-post-btn"
        >
          <Plus size={18} />
          Neuer Beitrag
        </Button>
      </div>

      {/* Posts List */}
      {!showEditor && (
        <div className="space-y-4">
          {posts.length === 0 ? (
            <div className="text-center py-12 text-[#666666] bg-white rounded-lg border border-[#E5E5E5]">
              <p>Noch keine Beiträge. Erstellen Sie Ihren ersten Blog-Beitrag!</p>
            </div>
          ) : (
            posts.map((post) => (
              <div
                key={post.id}
                className="bg-white border border-[#E5E5E5] rounded-lg p-4 flex flex-col sm:flex-row gap-4"
                data-testid={`post-${post.id}`}
              >
                {/* Thumbnail */}
                <div className="w-full sm:w-40 h-24 bg-[#F9F9F7] rounded-lg overflow-hidden shrink-0">
                  {post.featured_image ? (
                    <img
                      src={getImageUrl(post.featured_image)}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#999999]">
                      <Image size={24} />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-heading text-lg truncate">{post.title}</h3>
                      <p className="text-sm text-[#666666]">{formatDate(post.created_at)}</p>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${post.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {post.is_published ? 'Veröffentlicht' : 'Entwurf'}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleEdit(post)}
                    className="p-2 hover:bg-[#F9F9F7] rounded-lg transition-colors"
                    title="Bearbeiten"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                    title="Löschen"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Editor */}
      {showEditor && (
        <form onSubmit={handleSubmit} className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl">
              {editingPost ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}
            </h2>
            <div className="flex items-center gap-3">
              {/* Language Toggle */}
              <div className="flex border border-[#E5E5E5] rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setEditLang('de')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${editLang === 'de' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
                >
                  🇩🇪 DE
                </button>
                <button
                  type="button"
                  onClick={() => setEditLang('en')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${editLang === 'en' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
                >
                  🇬🇧 EN
                </button>
              </div>
              <button
                type="button"
                onClick={resetForm}
                className="p-2 hover:bg-[#F9F9F7] rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="overline block mb-2">
              Titel {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
            </label>
            <input
              type="text"
              value={editLang === 'en' ? formData.title_en : formData.title}
              onChange={(e) => setFormData({ ...formData, [editLang === 'en' ? 'title_en' : 'title']: e.target.value })}
              className="input-premium w-full"
              placeholder="Beitragstitel..."
              required={editLang === 'de'}
            />
          </div>

          {/* Featured Image */}
          <div>
            <label className="overline block mb-2">Beitragsbild (URL)</label>
            <input
              type="text"
              value={formData.featured_image}
              onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
              className="input-premium w-full"
              placeholder="https://..."
            />
            {formData.featured_image && (
              <img
                src={getImageUrl(formData.featured_image)}
                alt="Preview"
                className="mt-2 h-32 rounded-lg object-cover"
              />
            )}
          </div>

          {/* Content Editor */}
          <div>
            <label className="overline block mb-2">
              Inhalt {editLang === 'en' && <span className="text-blue-600">(EN)</span>}
            </label>
            
            {/* Toolbar */}
            <div className="flex flex-wrap gap-1 p-2 bg-[#F9F9F7] rounded-t-lg border border-b-0 border-[#E5E5E5]">
              <button
                type="button"
                onClick={() => execCommand('bold')}
                className="p-2 hover:bg-white rounded transition-colors"
                title="Fett"
              >
                <Bold size={16} />
              </button>
              <button
                type="button"
                onClick={() => execCommand('italic')}
                className="p-2 hover:bg-white rounded transition-colors"
                title="Kursiv"
              >
                <Italic size={16} />
              </button>
              <div className="w-px bg-[#E5E5E5] mx-1" />
              <select
                onChange={(e) => execCommand('fontSize', e.target.value)}
                className="px-2 py-1 rounded border border-[#E5E5E5] text-sm"
              >
                <option value="3">Normal</option>
                <option value="1">Klein</option>
                <option value="4">Mittel</option>
                <option value="5">Groß</option>
                <option value="6">Sehr groß</option>
              </select>
              <div className="w-px bg-[#E5E5E5] mx-1" />
              <button
                type="button"
                onClick={() => execCommand('formatBlock', 'h2')}
                className="p-2 hover:bg-white rounded transition-colors text-sm font-bold"
                title="Überschrift"
              >
                H2
              </button>
              <button
                type="button"
                onClick={() => execCommand('formatBlock', 'h3')}
                className="p-2 hover:bg-white rounded transition-colors text-sm font-bold"
                title="Unterüberschrift"
              >
                H3
              </button>
              <div className="w-px bg-[#E5E5E5] mx-1" />
              <button
                type="button"
                onClick={insertImage}
                className="p-2 hover:bg-white rounded transition-colors"
                title="Bild einfügen"
              >
                <Image size={16} />
              </button>
            </div>
            
            {/* Editable Area */}
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning={true}
              dir="ltr"
              className="min-h-[300px] p-4 border border-[#E5E5E5] rounded-b-lg focus:outline-none focus:ring-2 focus:ring-gold/20 prose max-w-none text-left"
              style={{ direction: 'ltr', textAlign: 'left' }}
              onInput={handleContentChange}
            />
          </div>

          {/* Published Toggle */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
            </label>
            <span className="text-sm">
              {formData.is_published ? 'Veröffentlicht' : 'Entwurf'}
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-[#E5E5E5]">
            <Button type="submit" className="btn-primary flex items-center gap-2">
              <Save size={18} />
              Speichern
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>
              Abbrechen
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
